import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Button,
  DataTable,
  EmptyState,
  Flex,
  StatusBadge,
  Text,
  TruncatedText,
  type MultiSelectOption,
  type StatusName,
} from '@chatsdet/ui';
import { CheckCircle, Circle, XCircle, WarningCircle } from '@phosphor-icons/react';
import { formatDurationMs } from '../../../lib/formatDuration';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import type { ExternalRunDetailSearchParams } from '../../../router';
import type {
  ExternalScenarioOverviewDto,
  RunSummaryCountsDto,
} from '../../../features/external-tests/useExternalRun';
import { ExternalTestsFilterBar } from './ExternalTestsFilterBar';

const VERDICT = {
  passed: {
    Icon: CheckCircle,
    className: 'text-status-success',
    label: 'Passed',
    status: 'success',
  },
  failed: { Icon: XCircle, className: 'text-status-failed', label: 'Failed', status: 'failed' },
  flaky: { Icon: WarningCircle, className: 'text-status-skipped', label: 'Flaky', status: 'flaky' },
  skipped: { Icon: Circle, className: 'text-status-skipped', label: 'Skipped', status: 'skipped' },
} as const satisfies Record<
  ExternalScenarioOverviewDto['verdict'],
  { Icon: unknown; className: string; label: string; status: StatusName }
>;

const VERDICT_ORDER = ['passed', 'failed', 'flaky', 'skipped'] as const;

/**
 * The verdicts worth offering as a filter, in a fixed display order. Reads off
 * `summary.counts` rather than a scenario list — `summary.items` is empty for
 * the whole of a multi-area run (see `useExternalRun`'s docstring), so a count
 * of >0 is the only signal available for "does this run have any of this
 * verdict".
 */
function buildVerdictOptions(counts: RunSummaryCountsDto): MultiSelectOption[] {
  const present: Record<ExternalScenarioOverviewDto['verdict'], number> = {
    passed: counts.passed,
    failed: counts.failed,
    flaky: counts.flaky,
    skipped: counts.skipped,
  };
  return VERDICT_ORDER.filter((verdict) => present[verdict] > 0).map((verdict) => ({
    id: verdict,
    label: VERDICT[verdict].label,
  }));
}

/** The scenario columns for the run's flat, server-paginated results table. */
function useExternalTestColumns(): ColumnDef<StockFeatures, ExternalScenarioOverviewDto>[] {
  return React.useMemo<ColumnDef<StockFeatures, ExternalScenarioOverviewDto>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Test',
        enableSorting: true,
        cell: ({ row }) => {
          const scenario = row.original;
          const { Icon, className } = VERDICT[scenario.verdict];
          return (
            <Flex align="center" gap="sm" className="min-w-0">
              <Icon size={16} weight="fill" className={`${className} shrink-0`} />
              <TruncatedText text={scenario.label} className="text-sm" />
            </Flex>
          );
        },
        meta: { flex: 1 },
      },
      {
        accessorKey: 'firstFailureMessage',
        header: 'Failure',
        cell: ({ row }) => (
          <TruncatedText
            text={row.original.firstFailureMessage?.split('\n')[0] ?? '—'}
            className="text-xs text-status-failed max-w-[28rem]"
          />
        ),
      },
      {
        id: 'steps',
        accessorFn: (scenario) => scenario.nativeStepCount ?? scenario.total,
        header: 'Steps',
        enableSorting: true,
        cell: ({ row }) => (
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {row.original.nativeStepCount ?? row.original.total}
          </Text>
        ),
        meta: { flex: 0.1, minWidth: 92, maxWidth: 92 },
      },
      {
        accessorKey: 'verdict',
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => {
          const verdict = VERDICT[row.original.verdict];
          return <StatusBadge status={verdict.status} label={verdict.label} size="sm" />;
        },
        meta: { flex: 0.1, minWidth: 110, maxWidth: 110 },
      },
      {
        accessorKey: 'durationMs',
        header: 'Duration',
        enableSorting: true,
        cell: ({ row }) => (
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {row.original.durationMs != null ? formatDurationMs(row.original.durationMs) : '—'}
          </Text>
        ),
        meta: { flex: 0.1, minWidth: 120, maxWidth: 120 },
      },
    ],
    []
  );
}

/** The run's server-paginated results, one row per ticketed scenario. */
export function ExternalTestsTable({
  runId,
  scenarios,
  counts,
  page,
  pageSize,
  pageCount,
  totalItems,
  isLoading,
  filters,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
}: {
  runId: string;
  scenarios: ExternalScenarioOverviewDto[];
  /** The run's (or selected area's) verdict counts — used only to derive which verdicts are worth offering as a filter. */
  counts: RunSummaryCountsDto;
  page: number;
  pageSize: number;
  pageCount: number;
  totalItems: number;
  isLoading: boolean;
  filters: ExternalRunDetailSearchParams;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onFilterChange: (updates: Partial<ExternalRunDetailSearchParams>) => void;
}) {
  const buildHref = useRowLinkBuilder();

  const verdictOptions = React.useMemo(() => buildVerdictOptions(counts), [counts]);

  const columns = useExternalTestColumns();

  // Locked up (toolbar fused onto the table) whenever there are rows beneath
  // it. Deliberately NOT gated on `isLoading`: that is the 3s poll's
  // `isFetching`, so folding it in here would detach the toolbar and re-add the
  // wrapper margin on every background refetch — a visible twitch every tick.
  const isTableLockup = scenarios.length > 0;

  const handleClearFilters = React.useCallback(() => {
    onFilterChange({ q: undefined, verdicts: undefined, page: 1 });
  }, [onFilterChange]);

  return (
    <div className={isTableLockup ? undefined : 'mt-3'}>
      <ExternalTestsFilterBar
        filters={filters}
        onFilterChange={onFilterChange}
        verdictOptions={verdictOptions}
        attached={isTableLockup}
      />

      {totalItems === 0 ? (
        <EmptyState
          className="mt-3"
          title="No tests match these filters"
          description="Try a different search or status, or clear the filters to see the whole run."
          action={
            <Button variant="outline" size="sm" onPress={handleClearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={scenarios}
          aria-label="External test results"
          getRowHref={(scenario) =>
            buildHref({
              to: '/runs/external/$runId/item/$itemId',
              params: { runId, itemId: scenario.routeItemId },
            })
          }
          preservePageOnDataChange
          containerClassName={
            isTableLockup ? 'rounded-t-none border-t-0 filter-lockup-table' : undefined
          }
          serverPagination={{
            page,
            pageSize,
            pageCount,
            totalItems,
            isLoading,
            pageSizeOptions: [20, 50, 100],
            onPageChange,
            onPageSizeChange,
          }}
        />
      )}
    </div>
  );
}
